'use strict';

const {div, iframe, canvas} = require('iblokz-snabbdom-helpers');
const WaveSurfer = require('wavesurfer.js').default || require('wavesurfer.js');
const {isDirectFile} = require('./util');

const soundcloudEmbed = (url, {strip = false} = {}) => {
	const base = `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}`
		// Site accent — SC uses this for progress / playhead
		+ '&color=%232B95D6'
		+ '&auto_play=false'
		+ '&hide_related=true'
		+ '&show_comments=false'
		+ '&show_reposts=false'
		+ '&visual=false'
		+ '&show_teaser=false';
	if (strip) {
		// Classic widget (not mini) so the waveform is expanded; chrome stripped.
		return base
			+ '&show_artwork=false'
			+ '&show_user=false'
			+ '&show_playcount=false'
			+ '&buying=false'
			+ '&sharing=false'
			+ '&download=false';
	}
	return base + '&show_user=true';
};

// `*` is required: Mixcloud nests player-widget.mixcloud.com (different origin).
// Without it, parent Widget API play() stays autoplay-blocked until an iframe gesture.
const MC_IFRAME_ALLOW = 'autoplay *; encrypted-media *; fullscreen *; clipboard-write *';
// Always log Mixcloud in/out triggers while we debug unlock; tighten later.
const MC_DEBUG = true;

const mcLog = (hostEl, msg, extra) => {
	if (!MC_DEBUG && !(hostEl && hostEl.__mcDebug)) {
		return;
	}
	const line = `[mtbg:mixcloud] ${msg}`;
	if (extra !== undefined) {
		console.log(line, extra);
	} else {
		console.log(line);
	}
};

const mixcloudEmbed = (url, {strip = false} = {}) => {
	const feed = encodeURIComponent(url);
	const opts = 'hide_cover=1&hide_artwork=1&light=1';
	if (strip) {
		return `https://www.mixcloud.com/widget/iframe/?${opts}&mini=1&feed=${feed}`;
	}
	return `https://www.mixcloud.com/widget/iframe/?${opts}&feed=${feed}`;
};

let scApiPromise = null;
const loadScWidgetApi = () => {
	if (typeof window === 'undefined') {
		return Promise.resolve(null);
	}
	if (window.SC && window.SC.Widget) {
		return Promise.resolve(window.SC.Widget);
	}
	if (scApiPromise) {
		return scApiPromise;
	}
	scApiPromise = new Promise((resolve, reject) => {
		const existing = document.querySelector('script[data-mtbg-sc-widget]');
		if (existing) {
			existing.addEventListener('load', () => resolve(window.SC && window.SC.Widget));
			existing.addEventListener('error', reject);
			return;
		}
		const script = document.createElement('script');
		script.src = 'https://w.soundcloud.com/player/api.js';
		script.async = true;
		script.dataset.mtbgScWidget = '1';
		script.onload = () => resolve(window.SC && window.SC.Widget);
		script.onerror = reject;
		document.head.appendChild(script);
	});
	return scApiPromise;
};

let mcApiPromise = null;
const loadMcWidgetApi = () => {
	if (typeof window === 'undefined') {
		return Promise.resolve(null);
	}
	if (window.Mixcloud && window.Mixcloud.PlayerWidget) {
		return Promise.resolve(window.Mixcloud.PlayerWidget);
	}
	if (mcApiPromise) {
		return mcApiPromise;
	}
	mcApiPromise = new Promise((resolve, reject) => {
		const existing = document.querySelector('script[data-mtbg-mc-widget]');
		if (existing) {
			existing.addEventListener('load', () =>
				resolve(window.Mixcloud && window.Mixcloud.PlayerWidget));
			existing.addEventListener('error', reject);
			return;
		}
		const script = document.createElement('script');
		script.src = 'https://widget.mixcloud.com/media/js/widgetApi.js';
		script.async = true;
		script.dataset.mtbgMcWidget = '1';
		script.onload = () => resolve(window.Mixcloud && window.Mixcloud.PlayerWidget);
		script.onerror = reject;
		document.head.appendChild(script);
	});
	return mcApiPromise;
};

const bindScWidget = (iframeEl, hostEl) => {
	if (!iframeEl || !hostEl) {
		return;
	}
	loadScWidgetApi().then(Widget => {
		if (!Widget || !iframeEl.isConnected) {
			return;
		}
		const widget = Widget(iframeEl);
		hostEl.__sc = widget;
		hostEl.__scReady = false;
		const emit = (paused, extra = {}) => {
			hostEl.dispatchEvent(new CustomEvent('mtbgPlayback', {
				bubbles: true,
				detail: Object.assign({paused}, extra)
			}));
		};
		widget.bind(Widget.Events.READY, () => {
			hostEl.__scReady = true;
		});
		widget.bind(Widget.Events.PLAY, () => emit(false));
		widget.bind(Widget.Events.PAUSE, () => emit(true));
		widget.bind(Widget.Events.FINISH, () => emit(true, {ended: true}));
	}).catch(() => {});
};

const bindMcWidget = (iframeEl, hostEl) => {
	if (!iframeEl || !hostEl || hostEl.__mcBound) {
		return;
	}
	hostEl.__mcBound = true;
	hostEl.__mcDebug = MC_DEBUG;
	let attachGen = 0;

	const setArmed = (armed) => {
		const next = Boolean(armed);
		if (hostEl.__mcUnlocked === next) {
			return;
		}
		hostEl.__mcUnlocked = next;
		hostEl.classList.toggle('is-mc-unlocked', next);
		mcLog(hostEl, next ? 'armed' : 'disarmed');
	};

	const attach = () => {
		const gen = ++attachGen;
		mcLog(hostEl, 'iframe load → attach PlayerWidget', {gen});
		loadMcWidgetApi().then(PlayerWidget => {
			if (!PlayerWidget || !iframeEl.isConnected || gen !== attachGen) {
				return;
			}
			const widget = PlayerWidget(iframeEl);
			hostEl.__mc = widget;
			hostEl.__mcReady = false;
			const emit = (paused, extra = {}) => {
				mcLog(hostEl, paused ? 'event → pause' : 'event → play', extra);
				hostEl.dispatchEvent(new CustomEvent('mtbgPlayback', {
					bubbles: true,
					detail: Object.assign({paused}, extra)
				}));
			};
			const setProgress = (position, duration) => {
				if (!duration) {
					return;
				}
				hostEl.__mcProgress = Math.max(0, Math.min(1, position / duration));
				if (typeof hostEl.__mcPaintWave === 'function') {
					hostEl.__mcPaintWave();
				}
			};
			const onReady = (api) => {
				if (gen !== attachGen) {
					return;
				}
				hostEl.__mc = api || widget;
				hostEl.__mcReady = true;
				mcLog(hostEl, 'READY', {
					methods: Object.keys(hostEl.__mc).filter(k => typeof hostEl.__mc[k] === 'function')
				});
				const mc = hostEl.__mc;
				if (mc.events) {
					if (mc.events.play) {
						mc.events.play.on(() => {
							setArmed(true);
							emit(false);
						});
					}
					if (mc.events.pause) {
						mc.events.pause.on(() => emit(true));
					}
					if (mc.events.ended) {
						mc.events.ended.on(() => {
							hostEl.__mcProgress = 0;
							if (typeof hostEl.__mcPaintWave === 'function') {
								hostEl.__mcPaintWave();
							}
							emit(true, {ended: true});
						});
					}
					if (mc.events.progress) {
						mc.events.progress.on((position, duration) => {
							setProgress(position, duration);
						});
					}
				}
				// Arm immediately: with allow="autoplay *" parent play/seek work without
				// an iframe gesture. Keep the wave capturing clicks so Mixcloud links
				// underneath never open a new tab.
				setArmed(true);
				if (typeof mc.getDuration === 'function') {
					mc.getDuration().then(d => mcLog(hostEl, 'armed duration', d)).catch(() => {});
				}
				if (hostEl.__mcPendingPlay && typeof mc.play === 'function') {
					hostEl.__mcPendingPlay = false;
					mcLog(hostEl, 'flush pending play()');
					mc.play().then(() => mcLog(hostEl, 'pending play() resolved'))
						.catch(err => {
							hostEl.__mcPendingPlay = true;
							mcLog(hostEl, 'pending play() failed', String(err));
						});
				}
			};
			if (widget.ready && typeof widget.ready.then === 'function') {
				widget.ready.then(onReady).catch(err => mcLog(hostEl, 'ready failed', String(err)));
			} else {
				onReady(widget);
			}
		}).catch(err => mcLog(hostEl, 'Widget API load failed', String(err)));
	};

	iframeEl.addEventListener('load', attach);
};

const hashSeed = (str = '') => {
	let h = 2166136261;
	for (let i = 0; i < str.length; i++) {
		h ^= str.charCodeAt(i);
		h = Math.imul(h, 16777619);
	}
	return h >>> 0;
};

/** Decorative bars — Mixcloud has no public peaks; real unlock is iframe gesture. */
const paintMixcloudWave = (canvas, seedStr, progress = 0) => {
	if (!canvas || !canvas.getContext) {
		return;
	}
	const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
	const cssW = Math.max(1, canvas.clientWidth || (canvas.parentElement && canvas.parentElement.clientWidth) || 300);
	const cssH = Math.max(1, canvas.clientHeight || 63);
	canvas.width = Math.floor(cssW * dpr);
	canvas.height = Math.floor(cssH * dpr);
	const ctx = canvas.getContext('2d');
	ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	ctx.fillStyle = '#f6f6f6';
	ctx.fillRect(0, 0, cssW, cssH);
	const barW = 2;
	const gap = 1;
	const step = barW + gap;
	const split = Math.max(0, Math.min(1, progress)) * cssW;
	let seed = hashSeed(seedStr);
	const rand = () => {
		seed = (Math.imul(seed, 1664525) + 1013904223) >>> 0;
		return seed / 4294967296;
	};
	for (let x = 0; x < cssW; x += step) {
		const amp = 0.18 + rand() * 0.72;
		const h = Math.max(2, cssH * amp * 0.85);
		const y = (cssH - h) / 2;
		ctx.fillStyle = x < split ? '#333' : '#8a8a8a';
		ctx.fillRect(x, y, barW, h);
	}
};

const mountMixcloudWave = (hostEl, url) => {
	const canvasEl = hostEl && hostEl.querySelector('.mixcloud-wave-canvas');
	if (!canvasEl) {
		return;
	}
	const paint = () => paintMixcloudWave(canvasEl, url, hostEl.__mcProgress || 0);
	hostEl.__mcPaintWave = paint;
	paint();
	if (typeof ResizeObserver === 'undefined') {
		return;
	}
	if (hostEl.__mcWaveRo) {
		hostEl.__mcWaveRo.disconnect();
	}
	hostEl.__mcWaveRo = new ResizeObserver(() => paint());
	hostEl.__mcWaveRo.observe(hostEl);
};

module.exports = ({
	track = {},
	height = 72,
	mediaControls = true,
	interact = true
}) => {
	const url = track.url || '';
	const source = track.source || '';
	const strip = height < 48 || mediaControls === false;
	const isSoundcloud = source === 'soundcloud' || /soundcloud\.com/.test(url);
	const isMixcloud = source === 'mixcloud' || /mixcloud\.com/.test(url);

	if (isSoundcloud && strip) {
		return div('.media-player.audio.soundcloud.soundcloud-strip', {
			key: `sc-strip:${url}`,
			hook: {
				insert: vnode => {
					const frame = vnode.elm.querySelector('iframe');
					bindScWidget(frame, vnode.elm);
				},
				update: (oldVnode, vnode) => {
					vnode.elm.__sc = oldVnode.elm.__sc;
					vnode.elm.__scReady = oldVnode.elm.__scReady;
				},
				destroy: vnode => {
					delete vnode.elm.__sc;
					delete vnode.elm.__scReady;
				}
			}
		}, [
			iframe({
				attrs: {
					src: soundcloudEmbed(url, {strip: true}),
					width: '100%',
					// Full classic height — CSS clips to the waveform band at row height
					height: '166',
					frameborder: 'no',
					scrolling: 'no',
					allow: 'autoplay'
				}
			})
		]);
	}

	if (!strip && isSoundcloud) {
		return div('.media-player.audio.soundcloud', {
			key: url
		}, [
			iframe({
				attrs: {
					src: soundcloudEmbed(url),
					frameborder: 'no',
					scrolling: 'no',
					allow: 'autoplay'
				}
			})
		]);
	}

	if (isMixcloud && strip) {
		// Wave overlay always captures clicks (iframe is pointer-events:none).
		// Tracks arm on Widget ready so seek/play go through our API, not Mixcloud links.
		return div('.media-player.audio.mixcloud.mixcloud-strip.is-mc-unlocked', {
			key: `mc-strip:${url}`,
			hook: {
				insert: vnode => {
					vnode.elm.__mcUnlocked = true;
					mountMixcloudWave(vnode.elm, url);
					const frame = vnode.elm.querySelector('iframe');
					bindMcWidget(frame, vnode.elm);
					mcLog(vnode.elm, 'strip mounted (pre-armed)', {url});
				},
				update: (oldVnode, vnode) => {
					vnode.elm.__mc = oldVnode.elm.__mc;
					vnode.elm.__mcReady = oldVnode.elm.__mcReady;
					vnode.elm.__mcPendingPlay = oldVnode.elm.__mcPendingPlay;
					vnode.elm.__mcProgress = oldVnode.elm.__mcProgress;
					vnode.elm.__mcWaveRo = oldVnode.elm.__mcWaveRo;
					vnode.elm.__mcPaintWave = oldVnode.elm.__mcPaintWave;
					vnode.elm.__mcBound = oldVnode.elm.__mcBound;
					vnode.elm.__mcUnlocked = oldVnode.elm.__mcUnlocked;
					vnode.elm.__mcDebug = oldVnode.elm.__mcDebug;
					vnode.elm.classList.toggle('is-mc-unlocked', Boolean(oldVnode.elm.__mcUnlocked));
					mountMixcloudWave(vnode.elm, url);
				},
				destroy: vnode => {
					if (vnode.elm.__mcWaveRo) {
						vnode.elm.__mcWaveRo.disconnect();
						delete vnode.elm.__mcWaveRo;
					}
					delete vnode.elm.__mc;
					delete vnode.elm.__mcReady;
					delete vnode.elm.__mcPendingPlay;
					delete vnode.elm.__mcProgress;
					delete vnode.elm.__mcPaintWave;
					delete vnode.elm.__mcBound;
					delete vnode.elm.__mcUnlocked;
					delete vnode.elm.__mcDebug;
				}
			}
		}, [
			div('.mixcloud-wave', [
				canvas('.mixcloud-wave-canvas', {
					attrs: {width: '300', height: '63'}
				})
			]),
			iframe({
				attrs: {
					src: mixcloudEmbed(url, {strip: true}),
					width: '100%',
					height: '60',
					frameborder: '0',
					scrolling: 'no',
					allow: MC_IFRAME_ALLOW
				}
			})
		]);
	}

	if (!strip && isMixcloud) {
		return div('.media-player.audio.mixcloud', {
			key: url,
			hook: {
				insert: vnode => {
					const frame = vnode.elm.querySelector('iframe');
					bindMcWidget(frame, vnode.elm);
				},
				update: (oldVnode, vnode) => {
					vnode.elm.__mc = oldVnode.elm.__mc;
					vnode.elm.__mcReady = oldVnode.elm.__mcReady;
					vnode.elm.__mcPendingPlay = oldVnode.elm.__mcPendingPlay;
					vnode.elm.__mcBound = oldVnode.elm.__mcBound;
				},
				destroy: vnode => {
					delete vnode.elm.__mc;
					delete vnode.elm.__mcReady;
					delete vnode.elm.__mcPendingPlay;
					delete vnode.elm.__mcBound;
				}
			}
		}, [
			iframe({
				attrs: {
					src: mixcloudEmbed(url),
					width: '100%',
					height: '120',
					frameborder: '0',
					scrolling: 'no',
					allow: MC_IFRAME_ALLOW
				}
			})
		]);
	}

	if (source === 'upload' || isDirectFile(url)) {
		return div('.media-player.audio.wavesurfer', {
			// stable key — do not remount on selection changes
			key: `${url}:${height}`,
			hook: {
				insert: vnode => {
					// Skip re-init if snabbdom reused the elm (shouldn't destroy, but be safe)
					if (vnode.elm.__ws) {
						return;
					}
					const ws = WaveSurfer.create({
						container: vnode.elm,
						url,
						height,
						waveColor: '#8a8a8a',
						progressColor: '#333',
						cursorColor: 'transparent',
						cursorWidth: 0,
						barWidth: 2,
						barGap: 1,
						normalize: true,
						mediaControls: false,
						interact
					});
					vnode.elm.__ws = ws;
					ws.on('ready', () => {
						const row = vnode.elm.closest('.widget-track-list-row');
						const root = vnode.elm.closest('.widget-track-list');
						if (!row || !root) {
							return;
						}
						const active = row.classList.contains('is-active');
						if (active && typeof ws.setOptions === 'function') {
							ws.setOptions({cursorWidth: 1, cursorColor: '#111'});
						}
						const media = ws.getMediaElement && ws.getMediaElement();
						if (media) {
							media.controls = false;
						}
						const slot = root.querySelector('.widget-track-list-transport');
						if (slot) {
							slot.__mtbgEmbedKey = null;
							slot.dispatchEvent(new CustomEvent('mtbgWsReady'));
						}
					});
				},
				update: (oldVnode, vnode) => {
					vnode.elm.__ws = oldVnode.elm.__ws;
				},
				destroy: vnode => {
					if (vnode.elm && vnode.elm.__ws) {
						vnode.elm.__ws.destroy();
						delete vnode.elm.__ws;
					}
				}
			}
		});
	}

	if (strip) {
		return div('.media-player.audio.wavesurfer');
	}

	return div('.media-player.audio.external', [
		iframe({
			attrs: {
				src: url,
				frameborder: '0'
			}
		})
	]);
};
